import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BadgeDollarSign, CalendarOff, Contact, LogOut, Receipt } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { formatMoney, formatMonth, lazyPage } from "@vixio/ui";
import "./api";
import { RUN_STATUS, type PayrollRun, type RoleCost } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const EmployeesPage = lazyPage(() => import("./EmployeesPage"), "EmployeesPage");
const EmployeePage = lazyPage(() => import("./EmployeePage"), "EmployeePage");
const PayrollPage = lazyPage(() => import("./PayrollPage"), "PayrollPage");
const RunPage = lazyPage(() => import("./PayrollPage"), "RunPage");
const AbsencesPage = lazyPage(() => import("./OtherPages"), "AbsencesPage");
const PositionsPage = lazyPage(() => import("./OtherPages"), "PositionsPage");
const TerminationsPage = lazyPage(() => import("./OtherPages"), "TerminationsPage");

function HrWidget() {
  const org = useOrg();
  const roles = useQuery({ queryKey: ["hr", "role_costs", org.id], queryFn: () => unwrap<RoleCost[]>(from("hr", "v_role_costs").select("*").eq("org_id", org.id).order("role_key")) });
  const run = useQuery({
    queryKey: ["hr", "runs", "latest", org.id],
    queryFn: async () => (await unwrap<PayrollRun[]>(from("hr", "payroll_runs").select("*").eq("org_id", org.id).eq("kind", "monthly").order("competence", { ascending: false }).limit(1)))[0] ?? null,
  });
  return (
    <dl className="vx-dl">
      {(roles.data ?? []).slice(0, 4).map((r) => (
        <div key={r.role_key} style={{ display: "contents" }}>
          <dt>Hora de {r.role_key}</dt>
          <dd className="num">{formatMoney(r.cost_per_hour_cents)}</dd>
        </div>
      ))}
      <dt>Folha</dt>
      <dd>
        {run.data ? (
          <Link to={`/rh/folha/${run.data.id}`}>
            {formatMonth(run.data.competence)} {RUN_STATUS[run.data.status]?.label}
          </Link>
        ) : (
          <Link to="/rh/folha">abrir competência</Link>
        )}
      </dd>
    </dl>
  );
}

export default defineModuleUi({
  key: "hr",
  title: "Pessoas e RH",
  domain: "people",
  icon: Contact,
  menu: [
    { path: "/rh/colaboradores", label: "Colaboradores", minRole: "manager", icon: Contact, order: 10, keywords: "rh admissão vínculo clt pj salário remuneração" },
    { path: "/rh/folha", label: "Folha de pagamento", minRole: "manager", icon: Receipt, order: 20, keywords: "competência holerite inss irrf fgts fechamento" },
    { path: "/rh/ausencias", label: "Férias e afastamentos", minRole: "manager", icon: CalendarOff, order: 30, keywords: "férias abono licença atestado" },
    { path: "/rh/cargos", label: "Cargos e custo por hora", minRole: "manager", icon: BadgeDollarSign, order: 40, keywords: "custo hora papel preço piso encargos regime" },
    { path: "/rh/rescisoes", label: "Rescisões", minRole: "manager", icon: LogOut, order: 50, keywords: "desligamento demissão aviso prévio multa fgts" },
  ],
  routes: [
    { path: "/rh/colaboradores", element: <EmployeesPage />, minRole: "manager" },
    { path: "/rh/colaboradores/:id", element: <EmployeePage />, minRole: "manager" },
    { path: "/rh/folha", element: <PayrollPage />, minRole: "manager" },
    { path: "/rh/folha/:id", element: <RunPage />, minRole: "manager" },
    { path: "/rh/ausencias", element: <AbsencesPage />, minRole: "manager" },
    { path: "/rh/cargos", element: <PositionsPage />, minRole: "manager" },
    { path: "/rh/rescisoes", element: <TerminationsPage />, minRole: "manager" },
  ],
  widgets: [{ key: "hr.costs", title: "Custo por hora e folha", minRole: "manager", span: 1, order: 70, element: HrWidget }],
});
